// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Divider, Grid, Group, Modal, NativeSelect, Stack, Text, TextInput } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { CPT, normalizeErrorString } from '@medplum/core';
import type { Address, ChargeItem, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { getPatientPaymentMethod } from '@polarissleep/shared';
import { IconCheck, IconEye, IconPlus } from '@tabler/icons-react';
import { type ChangeEvent, type FormEvent, type JSX, useMemo, useState } from 'react';
import { FormCard } from '../../../components/FormCard';
import { formatMoney } from '../../../utils/chargeitems';
import { withTenantScope } from '../../../utils/tenant';
import { AddCardModal } from './AddCardModal';
import { HistoryOfChargesTable } from './HistoryOfChargesTable';

export interface PaymentSectionProps {
  patient: Patient;
  chargeItems: ChargeItem[];
  loading: boolean;
  canManagePayments?: boolean;
  onPatientSaved?: (patient: Patient) => void;
  onChargeItemsChanged?: () => void;
}

const CHARGE_STATUS_OPTIONS: { value: NonNullable<ChargeItem['status']>; label: string }[] = [
  { value: 'planned', label: 'Planned' },
  { value: 'billable', label: 'Billable' },
  { value: 'billed', label: 'Billed' },
  { value: 'not-billable', label: 'Not billable' },
  { value: 'aborted', label: 'Aborted' },
  { value: 'entered-in-error', label: 'Entered in error' },
];

interface ChargeItemFormState {
  purpose: string;
  cptCode: string;
  status: NonNullable<ChargeItem['status']>;
  amount: string;
  occurrenceDate: string;
}

function chargeItemFormState(chargeItem: ChargeItem | undefined): ChargeItemFormState {
  const cpt = chargeItem?.code?.coding?.find((coding) => coding.system === CPT)?.code;
  return {
    purpose: chargeItem?.code?.text ?? chargeItem?.code?.coding?.[0]?.display ?? '',
    cptCode: cpt ?? '',
    status: chargeItem?.status ?? 'billable',
    amount: chargeItem?.priceOverride?.value == null ? '' : String(chargeItem.priceOverride.value),
    occurrenceDate: chargeItem?.occurrenceDateTime?.slice(0, 10) ?? '',
  };
}

function formatExpMonth(value: number | undefined): string {
  return value == null ? '**' : String(value).padStart(2, '0');
}

function formatAddress(address: Address | undefined): string {
  if (!address) {
    return '—';
  }
  return [address.line?.join(' '), address.city, address.state, address.postalCode, address.country]
    .filter(Boolean)
    .join(', ');
}

function parseCurrency(value: string): number | undefined | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const amount = Number(trimmed);
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
}

function buildChargeItem(patient: Patient, values: ChargeItemFormState, existing: ChargeItem | undefined): ChargeItem {
  const amount = parseCurrency(values.amount);
  const purpose = values.purpose.trim();
  const cptCode = values.cptCode.trim();
  const next: ChargeItem = existing
    ? { ...existing }
    : {
        resourceType: 'ChargeItem',
        status: values.status,
        subject: { reference: `Patient/${patient.id}` },
        code: {},
      };

  next.status = values.status;
  next.subject = { reference: `Patient/${patient.id}` };
  next.code = {
    text: purpose || cptCode,
    coding: cptCode ? [{ system: CPT, code: cptCode, display: purpose || undefined }] : undefined,
  };
  next.occurrenceDateTime = values.occurrenceDate ? `${values.occurrenceDate}T00:00:00.000Z` : undefined;

  if (amount == null) {
    delete next.priceOverride;
  } else {
    next.priceOverride = { value: amount, currency: 'USD' };
  }

  return next;
}

export function PaymentSection({
  patient,
  chargeItems,
  loading,
  canManagePayments = false,
  onPatientSaved,
  onChargeItemsChanged,
}: PaymentSectionProps): JSX.Element {
  const medplum = useMedplum();
  const paymentMethod = useMemo(() => getPatientPaymentMethod(patient), [patient]);
  const [detailsOpened, setDetailsOpened] = useState(false);
  const [cardEditorOpened, setCardEditorOpened] = useState(false);
  const [chargeEditorOpened, setChargeEditorOpened] = useState(false);
  const [editingChargeItem, setEditingChargeItem] = useState<ChargeItem | undefined>();
  const [chargeValues, setChargeValues] = useState<ChargeItemFormState>(() => chargeItemFormState(undefined));
  const [chargeSaving, setChargeSaving] = useState(false);

  const updateChargeValue =
    (field: keyof ChargeItemFormState) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
      const { value } = event.currentTarget;
      setChargeValues((current) => ({ ...current, [field]: value }));
    };

  const openChargeEditor = (chargeItem?: ChargeItem): void => {
    setEditingChargeItem(chargeItem);
    setChargeValues(chargeItemFormState(chargeItem));
    setChargeEditorOpened(true);
  };

  const saveChargeItem = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!patient.id) {
      return;
    }
    if (!chargeValues.purpose.trim() && !chargeValues.cptCode.trim()) {
      showNotification({ color: 'red', message: 'Enter a purpose or CPT/HCPCS code.' });
      return;
    }
    if (parseCurrency(chargeValues.amount) === null) {
      showNotification({ color: 'red', message: 'Enter a valid non-negative amount.' });
      return;
    }

    setChargeSaving(true);
    try {
      const next = withTenantScope(buildChargeItem(patient, chargeValues, editingChargeItem));
      if (editingChargeItem?.id) {
        await medplum.updateResource<ChargeItem>(next);
      } else {
        await medplum.createResource<ChargeItem>(next);
      }
      setChargeEditorOpened(false);
      setEditingChargeItem(undefined);
      onChargeItemsChanged?.();
      showNotification({
        color: 'green',
        icon: <IconCheck size={16} />,
        message: editingChargeItem ? 'Charge item updated' : 'Charge item added',
      });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setChargeSaving(false);
    }
  };

  const actionTitle = canManagePayments ? undefined : 'Requires admin or care coordinator access';

  return (
    <>
      <FormCard
        title="Payment"
        action={
          <Group gap="xs">
            <Button
              size="xs"
              variant="default"
              leftSection={<IconEye size={14} />}
              disabled={!canManagePayments}
              title={actionTitle}
              onClick={() => setDetailsOpened(true)}
            >
              Show Details
            </Button>
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              disabled={!canManagePayments || !patient.id}
              title={actionTitle}
              onClick={() => openChargeEditor()}
            >
              Add Charge Item
            </Button>
          </Group>
        }
      >
        <Stack gap="md">
          {paymentMethod ? (
            <>
              <Text fw={600}>
                {paymentMethod.brand} ending in {paymentMethod.last4}
              </Text>
              <Text size="sm" c="dimmed">
                Full card numbers are never stored or shown here. Use Stripe flow to replace card on file.
              </Text>
            </>
          ) : (
            <>
              <Text fw={600}>Payment method not on file.</Text>
              <Text size="sm" c="dimmed">
                Add card through Stripe to save masked details and billing address on patient chart.
              </Text>
            </>
          )}
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput
                label="Cardholder Name"
                value={paymentMethod?.cardholderName ?? '—'}
                readOnly
                aria-label="Cardholder Name"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput
                label="Card Number"
                value={paymentMethod ? `**** **** **** ${paymentMethod.last4}` : '**** **** **** ****'}
                readOnly
                aria-label="Card Number"
                rightSection={<IconEye size={14} />}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput
                label="Expiration Month"
                value={formatExpMonth(paymentMethod?.expMonth)}
                readOnly
                aria-label="Expiration Month"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput
                label="Expiration Year"
                value={paymentMethod?.expYear ? String(paymentMethod.expYear) : '****'}
                readOnly
                aria-label="Expiration Year"
              />
            </Grid.Col>
          </Grid>

          <Divider my="sm" />

          <Text fw={600} size="sm">
            History of Charges
          </Text>
          <HistoryOfChargesTable
            chargeItems={chargeItems}
            loading={loading}
            canEdit={canManagePayments}
            onEditChargeItem={openChargeEditor}
          />
        </Stack>
      </FormCard>

      <Modal opened={detailsOpened} onClose={() => setDetailsOpened(false)} title="Payment Details" centered size="lg">
        <Stack gap="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput label="Cardholder Name" value={paymentMethod?.cardholderName ?? '—'} readOnly />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput
                label="Card Number"
                value={paymentMethod ? `**** **** **** ${paymentMethod.last4}` : '—'}
                readOnly
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <TextInput label="Brand" value={paymentMethod?.brand ?? '—'} readOnly />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <TextInput label="Expiration Month" value={formatExpMonth(paymentMethod?.expMonth)} readOnly />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <TextInput
                label="Expiration Year"
                value={paymentMethod?.expYear ? String(paymentMethod.expYear) : '—'}
                readOnly
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <TextInput label="Billing Address" value={formatAddress(paymentMethod?.billingAddress)} readOnly />
            </Grid.Col>
          </Grid>
          <Group justify="flex-end">
            <Button disabled={!patient.id} onClick={() => setCardEditorOpened(true)}>
              {paymentMethod ? 'Edit via Stripe' : 'Add card'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={chargeEditorOpened}
        onClose={() => {
          setChargeEditorOpened(false);
          setEditingChargeItem(undefined);
        }}
        title={editingChargeItem ? 'Edit Charge Item' : 'Add Charge Item'}
        centered
        size="lg"
      >
        <form onSubmit={saveChargeItem}>
          <Stack gap="md">
            <TextInput label="Purpose" value={chargeValues.purpose} onChange={updateChargeValue('purpose')} />
            <Grid>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <TextInput
                  label="CPT/HCPCS Code"
                  value={chargeValues.cptCode}
                  onChange={updateChargeValue('cptCode')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <NativeSelect
                  label="Payment Status"
                  value={chargeValues.status}
                  onChange={updateChargeValue('status')}
                  data={CHARGE_STATUS_OPTIONS}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <TextInput label="Amount" value={chargeValues.amount} onChange={updateChargeValue('amount')} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <TextInput
                  label="Service Date"
                  type="date"
                  value={chargeValues.occurrenceDate}
                  onChange={updateChargeValue('occurrenceDate')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 8 }}>
                <TextInput
                  label="Current Balance"
                  value={
                    editingChargeItem
                      ? formatMoney(editingChargeItem.priceOverride?.value, editingChargeItem.priceOverride?.currency)
                      : '—'
                  }
                  readOnly
                />
              </Grid.Col>
            </Grid>
            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => {
                  setChargeEditorOpened(false);
                  setEditingChargeItem(undefined);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" loading={chargeSaving}>
                Save Charge Item
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {patient.id && (
        <AddCardModal
          opened={cardEditorOpened}
          patientId={patient.id}
          existingPaymentMethod={paymentMethod}
          onClose={() => setCardEditorOpened(false)}
          onSaved={(savedPatient) => {
            onPatientSaved?.(savedPatient);
            setCardEditorOpened(false);
            setDetailsOpened(false);
            showNotification({ color: 'green', icon: <IconCheck size={16} />, message: 'Card updated' });
          }}
        />
      )}
    </>
  );
}
